import { useContext, useState } from 'react';
import { Button, Flex, FloatButton, Input, message, Pagination, Select, Skeleton, Table, Tag, Tooltip, Typography } from 'antd';
import {
  ContextStatus,
  SubscriptionStatus,
  subscriptionStatusAsString,
} from '@leedpulse-monorepo/enums';
import { PlusOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import CompaniesContext from '../../contexts/data/companies/CompaniesContext';
import CompanyEditionFormContext, {
  ISubscriptionItem,
  SubscriptionType,
} from '../../contexts/data/companies/CompanyEditionFormContext';
import { advancedOnRow } from '../../../core/utils/commonUtils';
import CompanyManageSubscriptionsModal from '../../components/companies_page/Modals/CompanyManageSubscriptionsModal';
import CompanyAddSubscriptionModal from '../../components/companies_page/Modals/CompanyAddSubscriptionModal';
import { ICompany } from '@leedpulse-monorepo/interfaces';
import { SUBSCRIPTIONS_PAGE_PATH } from '../subscriptions/SubscriptionsPage';
import { formatPhoneNumber } from '@leedpulse-monorepo/utils';
import { TableRowSelection } from 'antd/es/table/interface';
import ResetDemoDataModal from '../../components/@shared/subscriptions/ResetDemoDataModal/ResetDemoDataModal';

const CompanySubscriptionsList: React.FC = () => {
  const navigate = useNavigate();
  const companiesContext = useContext(CompaniesContext);
  const companyEditionFormContext = useContext(CompanyEditionFormContext);

  const companyDoc = companiesContext.selected as ICompany;

  const [selectedSubscriptionsIds, setSelectedSubscriptionsIds] = useState<string[]>([]);

  const [isManageSubscriptionsModalOpen, setIsManageSubscriptionsModalOpen] = useState(false);
  const [isAddSubscriptionModalOpen, setIsAddSubscriptionModalOpen] = useState(false);
  const [isResetDemoDataModalOpen, setIsResetDemoDataModalOpen] = useState(false);

  const onPaginationChange = (page: number, size: number) => {
    if (size !== companyEditionFormContext.subscriptionsPageSize) {
      companyEditionFormContext.setSubscriptionsPageSize(size);
    } else {
      void companyEditionFormContext.goToSubscriptionsPage(page);
    }
  };

  const rowSelection: TableRowSelection<ISubscriptionItem> = {
    type: 'checkbox',
    onChange: (selectedRowKeys: React.Key[], selectedRows: ISubscriptionItem[]) => {
      setSelectedSubscriptionsIds(selectedRowKeys as string[]);
    },
    getCheckboxProps: (record: ISubscriptionItem) => ({
      name: record.id,
    }),
  };

  return (
    <>
      <Flex vertical gap={12}>
        <Flex align="center" justify="flex-start" gap={10}>
          <Input
            placeholder="Rechercher un abonnement"
            style={{ width: '18.75rem' }}
            value={companyEditionFormContext.subscriptionSearchDraft}
            onChange={(e) =>
              companyEditionFormContext.setSubscriptionSearchDraft(e.target.value)
            }
            allowClear
          />
          <Tooltip title="Gérer les abonnements">
            <Button type="default" icon={<SwapOutlined />} onClick={() => setIsManageSubscriptionsModalOpen(true)} />
          </Tooltip>
          <Tooltip title="Ajouter un abonnement">
            <Button type="default" icon={<PlusOutlined />} onClick={() => setIsAddSubscriptionModalOpen(true)} />
          </Tooltip>
          <Flex align="center" gap={6}>
            {companyEditionFormContext.subscriptionsTotalCount} abonnements {companyEditionFormContext.subscriptionTypeFilter !== 'all' && companyEditionFormContext.subscriptionTypeFilter === SubscriptionType.DEMO ? 'de démo' : 'classique'}
            {companyEditionFormContext.subscriptionTypeFilter === SubscriptionType.DEMO && selectedSubscriptionsIds.length > 0 && <span style={{ fontSize: '12px', color: '#888' }}>({selectedSubscriptionsIds.length} sélectionnés)</span>}
          </Flex>
        </Flex>

        <Flex align="center" justify="space-between">
          <Flex>
            <Select
              value={companyEditionFormContext.subscriptionTypeFilter}
              onChange={(value) =>
                companyEditionFormContext.setSubscriptionTypeFilter(value as SubscriptionType)
              }
              options={[
                { label: 'Tous', value: SubscriptionType.ALL },
                { label: 'Démo', value: SubscriptionType.DEMO },
                { label: 'Classique', value: SubscriptionType.REGULAR },
              ]}
              style={{ width: "11.25rem" }}
            />
          </Flex>
          <Pagination
            current={companyEditionFormContext.subscriptionsTablePage}
            pageSize={companyEditionFormContext.subscriptionsPageSize}
            showSizeChanger
            total={companyEditionFormContext.subscriptionsTotalCount}
            style={{ marginLeft: 'auto' }}
            onChange={onPaginationChange}
          />
        </Flex>

        {companyEditionFormContext.subscriptionsListStatus === ContextStatus.LOADING ? (
          <Skeleton active />
        ) : companyEditionFormContext.subscriptionsListStatus === ContextStatus.SUCCESS &&
          companyEditionFormContext.subscriptionsTotalCount > 0 ? (
          <Table
            pagination={{
              current: companyEditionFormContext.subscriptionsTablePage,
              pageSize: companyEditionFormContext.subscriptionsPageSize,
              showSizeChanger: true,
              total: companyEditionFormContext.subscriptionsTotalCount,
              position: ['bottomRight'],
              onChange: onPaginationChange,
            }}
            columns={[
              {
                title: 'Nom',
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: 'Numéros',
                dataIndex: 'sender',
                key: 'sender',
                render: (sender: string[]) => sender && sender.length > 0 ?
                  sender.map(sender => formatPhoneNumber(sender)).join(', ') :
                  <span style={{ color: 'var(--danger-color)' }}>N/A</span>,
              },
              {
                title: 'Plan',
                dataIndex: 'idPlan',
                key: 'idPlan',
                render: (idPlan: string) => idPlan ? <Tag>{idPlan}</Tag> : <Tag color="error">N/A</Tag>,
              },
              {
                title: 'Crédits',
                dataIndex: 'credits',
                key: 'credits',
                render: (_, record: ISubscriptionItem) => {
                  return (
                    <Flex align="center" gap={4}>
                      <Typography.Text style={record.credits < 0 ? {color: 'var(--danger-color)', fontWeight: '500'} : {}}>
                        {record.credits} / {record.planCredits}
                      </Typography.Text>
                    </Flex>
                  );
                },
              },
              {
                title: 'Statut',
                dataIndex: 'status',
                key: 'status',
                render: (status: SubscriptionStatus) => (
                  <Tag color={
                    status === SubscriptionStatus.CANCELLED
                      ? 'red'
                      : status === SubscriptionStatus.PAUSED || status === SubscriptionStatus.CANCELLING
                        ? 'orange'
                        : 'green'
                  }>
                    {subscriptionStatusAsString(status)}
                  </Tag>
                )
              },
              {
                title: 'Action',
                render: () =>
                  <Flex justify="center"><Button
                    type="link"
                    size="small"
                    style={{ background: 'none', border: 'none', padding: 0, color: '#1677ff' }}
                  >
                    Voir le détail
                  </Button>
                  </Flex>
              },
            ]}
            rowKey={(record) => record.id}
            dataSource={companyEditionFormContext.subscriptions}
            onRow={(record) =>
              advancedOnRow({
                url: `${SUBSCRIPTIONS_PAGE_PATH}/${record.id}`,
                navigate,
                className: 'table-row',
              })
            }
            rowSelection={companyEditionFormContext.subscriptionTypeFilter === SubscriptionType.DEMO ? rowSelection : undefined}
          />
        ) : companyEditionFormContext.subscriptionsListStatus === ContextStatus.SUCCESS ||
          companyEditionFormContext.subscriptionsListStatus === ContextStatus.IDLE ? (
          <p
            style={{ textAlign: 'center', fontSize: '14px', color: '#888' }}
          >
            Aucun abonnement disponible
          </p>
        ) : (
          <p
            style={{ textAlign: 'center', fontSize: '14px', color: 'red' }}
          >
            Erreur lors du chargement des abonnements
          </p>
        )}
      </Flex>

      {selectedSubscriptionsIds.length > 0 && (
        <FloatButton
          type="primary"
          onClick={() => setIsResetDemoDataModalOpen(true)}
          tooltip={<div>Reset les comptes de démo</div>}
          badge={{ count: selectedSubscriptionsIds.length }}
          icon={<ReloadOutlined />}
        />
      )}

      <CompanyManageSubscriptionsModal
        open={isManageSubscriptionsModalOpen}
        onCancel={() => setIsManageSubscriptionsModalOpen(false)}
        onSuccess={async () => {
          message.success('Abonnements de l\'entreprise mis à jour');
          setIsManageSubscriptionsModalOpen(false);
          await companiesContext.fetchAndSelectById(companyDoc.id);
          const updatedCompany = companyDoc;
          if (updatedCompany) {
            companyEditionFormContext.initWithCompany(updatedCompany);
          }
        }}
        okText="Appliquer"
        cancelText="Annuler"
        width={1100}
      />

      <CompanyAddSubscriptionModal
        open={isAddSubscriptionModalOpen}
        onCancel={() => setIsAddSubscriptionModalOpen(false)}
        onSuccess={(createdSubscriptions) => {
          setIsAddSubscriptionModalOpen(false);
          const updatedCompany: ICompany = {
            ...companyDoc,
            id_subscriptions: {
              ...companyDoc.id_subscriptions,
              ...Object.fromEntries(createdSubscriptions.map((id) => [id, { updated: Date.now() }])),
            },
          };
          companiesContext.select(updatedCompany);
          companyEditionFormContext.initWithCompany(updatedCompany);

          if (createdSubscriptions.length > 0) {
            navigate(`${SUBSCRIPTIONS_PAGE_PATH}/${createdSubscriptions[0]}?tab=2`);
          }
        }}
        okText="Commander"
        cancelText="Annuler"
        width={700}
      />

      <ResetDemoDataModal
        subscriptions={selectedSubscriptionsIds}
        open={isResetDemoDataModalOpen}
        resetCallback={() => setIsResetDemoDataModalOpen(false)}
        onCancel={() => setIsResetDemoDataModalOpen(false)}
      />
    </>
  );
};

export default CompanySubscriptionsList;
